import React from 'react';
import { useTranslation } from 'react-i18next';
import AddonServiceLines from './AddonServiceLines';
import { RentalBookingDetails } from '../types/bookingCreation';
import { useReservationSummary } from './reservationSummary/useReservationSummary';
import { breakdownToServiceLines } from './reservationSummary/breakdownLines';
import { PriceBreakdown } from '../api/booking/pricePreviewApiTypes';
import DiscountEditRow from './reservationSummary/DiscountEditRow';
import AlreadyPaidSection from './reservationSummary/AlreadyPaidSection';
import type { CoveredBy } from '../utils/bookingCoverage';
import TaxBreakdown from './reservationSummary/TaxBreakdown';
import PricePreviewStateRow, { PricePreviewStatus } from './reservationSummary/PricePreviewStateRow';

// Allows surcharges (negative discount) down to -1000, in line with the room twin.
const MIN_DISCOUNT_AMOUNT = -1000;

// Why the price box is empty before an object is picked — never a bare "n.z.".
const VENUE_IDLE_HINT = 'booking_details.reservation_summary.price_pending_venue_selection';

interface Props {
    bookingDetails?: RentalBookingDetails;
    isCreateBooking: boolean;
    isEditBooking: boolean;
    couponApplied?: number;
    // Persisted booking total — view mode only (booking-details responses don't
    // embed a PriceBreakdown yet). Create/edit ignore it and render `breakdown`.
    total?: number;
    amountPaid?: number;
    onDiscountChange?: (value: number) => void;
    onAlreadyPaidChange?: (value: number) => void;
    numberOfGuests?: number;
    startDate?: string;
    endDate?: string;
    // Backend price breakdown (#1203) — the single source for every price line and
    // total in create/edit mode. While it is absent a state row is shown instead
    // of the money rows; there is no FE-computed fallback.
    breakdown?: PriceBreakdown;
    // Why the breakdown is absent (idle / loading / error) — see PricePreviewStateRow.
    previewStatus?: PricePreviewStatus;
    /** Covered by an organization — replaces "Fälliger Betrag" (ADR-0008 2026-09-23). */
    coveredBy?: CoveredBy | null;
}

function VenueReservationSummary({
    isCreateBooking,
    isEditBooking,
    bookingDetails,
    couponApplied,
    total = 0,
    amountPaid,
    onDiscountChange,
    onAlreadyPaidChange,
    numberOfGuests = 0,
    startDate,
    endDate,
    breakdown,
    previewStatus = 'loading',
    coveredBy = null,
}: Props) {
    const { t } = useTranslation();

    // Every surface renders the BE breakdown (#1203) — view included. Until it
    // arrives, the money rows show a placeholder, never an FE number.
    const previewPending = !breakdown;

    // Format dates for display
    const formatDate = (dateInput?: string | any) => {
        if (!dateInput) return '';

        let date: Date;

        // Handle Dayjs objects
        if (dateInput && typeof dateInput === 'object' && dateInput.$d) {
            date = new Date(dateInput.$d);
        } else if (typeof dateInput === 'string') {
            date = new Date(dateInput);
        } else {
            return '';
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
        });
    };

    const summary = useReservationSummary({
        total,
        couponApplied,
        amountPaid,
        minDiscount: MIN_DISCOUNT_AMOUNT,
        onDiscountChange,
        onAlreadyPaidChange,
        breakdown,
    });

    const editable = isCreateBooking || isEditBooking;
    // Discount is measured against the pre-discount total (BE subtotal when present).
    const discountBound = breakdown ? breakdown.subtotal : total;

    return (
        <div className={'w-[90%] xl:w-[70%] col-span-12 md:col-span-4 border border-foreground/20 rounded py-4 px-3 mx-auto xl:ml-auto xl:mr-0 min-h-[32px]'}>
            {/* Reservation Details Section */}
            <div className={'rounded-lg mb-4'}>
                <h3 className={'text-sm font-semibold mb-3 text-black'}>{t('create_booking.reservation_details.title')}</h3>
                <div className={'space-y-2'}>
                    <div className={'flex justify-between text-sm'}>
                        <span>{t('create_booking.reservation_details.date')}:</span>
                        <span>
                            {isCreateBooking && startDate && endDate
                                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                                : bookingDetails?.checkIn && bookingDetails?.checkOut
                                    ? `${formatDate(bookingDetails.checkIn)} - ${formatDate(bookingDetails.checkOut)}`
                                    : t('common.unknown')}
                        </span>
                    </div>
                    <div className={'flex justify-between text-sm'}>
                        <span>{t('create_booking.reservation_details.number_of_guests')}</span>
                        <span>{bookingDetails?.numberOfGuests || numberOfGuests}</span>
                    </div>
                </div>
            </div>

            {/* Price Details Section */}
            <div className={'rounded-lg mb-4'}>
                <h3 className={'text-sm font-semibold mb-3 text-black'}>{t('booking_details.reservation_summary.price')}</h3>
                <div className={'space-y-2'}>
                    {/* BE breakdown (#1203): render every price line from one source. */}
                    {breakdown && (
                        <AddonServiceLines lines={breakdownToServiceLines(breakdown, t)} additionalService={0} />
                    )}

                    {/* No breakdown yet (idle / loading / error) — a state row, never an FE-computed number. */}
                    {previewPending
                        && <PricePreviewStateRow status={previewStatus} idleHintKey={VENUE_IDLE_HINT} />}

                    {couponApplied !== undefined && !previewPending && (
                        <DiscountEditRow summary={summary} total={discountBound} minDiscount={MIN_DISCOUNT_AMOUNT} editable={editable} />
                    )}

                    {!previewPending && (
                        <>
                            <div className={'border-b border-gray-300 my-2'}></div>

                            <div>
                                <TaxBreakdown
                                    displayTotal={summary.adjustedTotal}
                                    taxBreakdown={breakdown?.taxBreakdown}
                                    taxExemptionNote={breakdown?.taxExemptionNote}
                                    taxUnresolved={breakdown?.taxUnresolved}
                                    tax={summary.tax}
                                    net={summary.net}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Already-paid + amount-due (shared section). */}
            {!previewPending && amountPaid !== undefined && (
                <AlreadyPaidSection summary={summary} amountPaid={amountPaid} editable={isEditBooking} coveredBy={coveredBy} />
            )}
        </div>
    );
}

export default VenueReservationSummary;
